/**
 * An analog watch's `module` is its CALIBRE, not an electronic module.
 *
 * Casio numbers every movement it makes as a "module" — the analog quartz in a
 * Sheen or an Oceanus as much as the LCD engine in a G-Shock — and its extractor
 * reports that number in `module` whatever the display type. The importer reads
 * `module` as "electronic module" in several independent places: the module
 * check, the module step's stamp, the save-time match-or-create pass and
 * `importSingle`'s `moduleId` link. So an analog Oceanus imported as a ref
 * linked to a freshly-created electronic module, and its real calibre was never
 * offered to the calibre check at all.
 *
 * Gating each of those readers would leave the next one to re-open the bug, so
 * the entry is corrected ONCE, where it enters the pipeline: the number moves to
 * `movement` — which the calibre check lists and the calibre stamp matches on —
 * and `module` is dropped. Every reader downstream then sees an ordinary analog
 * entry.
 *
 * Only `analog`. A `digital-analog` (ana-digi) watch genuinely has an
 * electronic module, and `digital` is the case `module` exists for.
 */
import type { ScrapedWatchEntry } from './BulkImport';

/** Whether the entry's scraped display type is plain analog. */
export function isAnalogEntry(entry: Pick<ScrapedWatchEntry, 'movementType'>): boolean {
  // Trimmed and case-folded like the importer's own `=== 'digital'` checks.
  // The Casio extractor emits lowercase already; other sources may not.
  return (entry.movementType || '').trim().toLowerCase() === 'analog';
}

/**
 * The entry with an analog `module` moved into `movement`. Returns the SAME
 * object when there is nothing to change.
 *
 * An existing `movement` wins: it is the scraped calibre string when a source
 * has one, and the module number would only duplicate it. The module is
 * dropped either way — an analog watch has no electronic module to link.
 */
export function routeAnalogModuleToMovement<T extends ScrapedWatchEntry>(entry: T): T {
  const moduleStr = (entry.module || '').trim();
  if (!moduleStr || !isAnalogEntry(entry)) return entry;

  const { module: _dropped, ...rest } = entry;
  const movement = (entry.movement || '').trim() ? entry.movement : moduleStr;
  return { ...rest, movement } as T;
}
